import { createPermissionPayload, Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { normalizedEmailFilter } from '@nsc/smartteams-shared/api/email-filter.js';
import { SmartTeamsAppUserGroup } from '@nsc/smartteams-shared/db/SmartTeamsAppUserGroup.js';
import { SmartTeamsCustomer } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { upsertSmartTeamsAppUser } from '@nsc/smartteams-shared/api/upsert-app-user.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission, SmartTeamsRoleNames } from '../features/permissions/roles-and-permissions.js';
import { sendCustomerOnboardingEmail } from './send-customer-onboarding-email.js';

/**
 * Mirror of `createFieldEngineerWithUser` for the Customer userGroup. Creates (or reuses)
 * an AdornisUser, links a SmartTeamsAppUser with userGroup=Customer + the supplied
 * customerIds, assigns the Customer role, and sends an onboarding email.
 *
 * Not yet surfaced in any UI — kept here so the next ticket ("Create customer login"
 * button) only needs a button, not the whole pipeline.
 *
 * The shared `upsertSmartTeamsAppUser` helper enforces the userGroup-match guard, so this
 * throws if the user is already onboarded as a non-Customer (e.g. an FE accidentally
 * sharing an email with a customer).
 */
export const createCustomerWithUser = registerMutation({
  operationName: 'createCustomerWithUser',
  type: () => String,
  params: [
    { name: 'email', type: () => String },
    { name: 'password', type: () => String },
    { name: 'customerIds', type: () => [String] },
    { name: 'primaryLanguage', type: () => String },
  ],
  resolve:
    (email: string, password?: string, customerIds?: string[], primaryLanguage?: string) =>
    async (): Promise<string> => {
      const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
      if (!allowed) throw new ExpectedError('No permission to create customer logins');

      if (!email) throw new ExpectedError('Email is required to create a customer login');
      const normalizedEmail = email.toLocaleLowerCase();

      const ids = [...new Set(customerIds ?? [])];

      const customerCol = await getRawCollection<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName);
      if (ids.length > 0) {
        const found = await customerCol.find({ _id: { $in: ids } }, { projection: { _id: 1 } }).toArray();
        if (found.length !== ids.length) {
          const missing = ids.filter(id => !found.some(c => c._id === id));
          throw new ExpectedError(`Unknown customer IDs: ${missing.join(', ')}`);
        }
      }

      const userCol = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
      const matchingUsers = await userCol
        .find(normalizedEmailFilter(normalizedEmail), { projection: { _id: 1 } })
        .limit(2)
        .toArray();
      if (matchingUsers.length > 1) {
        throw new ExpectedError(
          `Duplicate user accounts differing only in case exist for ${normalizedEmail} and must be merged manually`,
        );
      }
      const existingUser = matchingUsers[0];

      let userId: string;
      let registeredFreshUser = false;
      if (existingUser?._id) {
        userId = existingUser._id;
      } else if (password) {
        userId = await createNewUser(
          new AdornisUser({ username: normalizedEmail, email: normalizedEmail }),
          password,
        );
        registeredFreshUser = true;
      } else {
        throw new ExpectedError('No user with that email — provide a password to create one');
      }

      await upsertSmartTeamsAppUser({
        userId,
        userGroup: SmartTeamsAppUserGroup.Customer,
        username: normalizedEmail,
        email: normalizedEmail,
        primaryLanguage,
        customerIds: ids,
      });

      // Assign Customer role.
      const roleCol = await getRawCollection<Role>(Role._collectionName);
      let customerRole = await roleCol.findOne({ roleName: SmartTeamsRoleNames.CUSTOMER });
      if (!customerRole) {
        customerRole = new Role({ roleName: SmartTeamsRoleNames.CUSTOMER, permissions: [] });
        await customerRole.create();
        logger.info('Created placeholder Customer role on demand');
      }
      const raCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
      const ra = await raCol.findOne({ userId });
      if (!ra) {
        await new RoleAssignment({ userId, roleIds: [customerRole._id] }).create();
      } else if (!ra.roleIds?.includes(customerRole._id)) {
        await raCol.updateOne({ _id: ra._id }, { $addToSet: { roleIds: customerRole._id } });
      }

      if (registeredFreshUser) {
        // User + customer links are already committed; a failed onboarding mail must not abort and
        // strand a half-created account.
        try {
          await sendCustomerOnboardingEmail(userId, password)();
        } catch (error: unknown) {
          logger.warn({ userId, error }, 'Customer user created, but onboarding email failed');
        }
      }

      return userId;
    },
});
